'use client';

import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card'; // Fixed import
import { FileText, Heart, Settings, PlusCircle, Loader2 } from 'lucide-react';

interface Inquiry {
    id: string;
    pet: {
        name: string;
        image_url: string;
        breed: string;
        id: string;
    };
    status: 'pending' | 'reviewing' | 'approved' | 'rejected' | 'completed';
    created_at: string;
}

export default function DashboardPage() {
    const { user, userProfile } = useAuth();
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const supabase = createClient();

    useEffect(() => {
        async function fetchDashboardData() {
            if (!user) return;

            try {
                // Fetch inquiries with pet details
                const { data, error } = await supabase
                    .from('adoption_inquiries')
                    .select(`
            id,
            status,
            created_at,
            pet:pets (
              id,
              name,
              image_url,
              breed
            )
          `)
                    .eq('user_id', user.id)
                    .order('created_at', { ascending: false });

                if (error) {
                    console.error('Error fetching inquiries:', error);
                } else {
                    // data.pet is an object or array depending on relationship, usually object for belongs_to
                    // TypeScript might complain about the shape, so we cast if needed or let inference handle it
                    setInquiries(data as unknown as Inquiry[]);
                }
            } catch (err) {
                console.error('Unexpected error:', err);
            } finally {
                setLoading(false);
            }
        }

        fetchDashboardData();
    }, [user]);

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-headline font-bold text-foreground">
                            Hello, {userProfile?.full_name || 'Friend'}!
                        </h1>
                        <p className="text-muted-foreground">
                            Welcome to your personal dashboard
                        </p>
                    </div>
                    <Link href="/pet-discovery">
                        <Button>
                            <PlusCircle className="mr-2 h-4 w-4" />
                            Find a New Pet
                        </Button>
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Quick Stats/Links */}
                    <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                            <FileText className="h-6 w-6" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">{inquiries.length}</h3>
                            <p className="text-sm text-muted-foreground">Active Applications</p>
                        </div>
                    </div>

                    <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                        <div className="p-3 bg-pink-100 text-pink-600 rounded-full">
                            <Heart className="h-6 w-6" />
                        </div>
                        <div>
                            {/* Placeholder for saved pets count */}
                            <h3 className="font-semibold text-lg">0</h3>
                            <p className="text-sm text-muted-foreground">Saved Pets</p>
                        </div>
                    </div>

                    <Link href="/settings" className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer">
                        <div className="p-3 bg-gray-100 text-gray-600 rounded-full">
                            <Settings className="h-6 w-6" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">Settings</h3>
                            <p className="text-sm text-muted-foreground">Manage Account</p>
                        </div>
                    </Link>
                </div>

                {/* Applications Section */}
                <div className="space-y-6">
                    <h2 className="text-2xl font-headline font-semibold text-foreground">
                        Your Adoption Applications
                    </h2>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : inquiries.length === 0 ? (
                        <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
                            <div className="mx-auto h-12 w-12 text-muted-foreground mb-4">
                                <FileText className="h-full w-full" />
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-2">No applications yet</h3>
                            <p className="text-muted-foreground mb-6">
                                You haven't applied to adopt any pets yet.
                            </p>
                            <Link href="/pet-discovery">
                                <Button variant="outline">Browse Available Pets</Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {inquiries.map((inquiry) => (
                                <Card key={inquiry.id} className="flex flex-col md:flex-row gap-4 p-4 items-center">
                                    <div className="h-20 w-20 relative flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                                        {inquiry.pet?.image_url ? (
                                            <img
                                                src={inquiry.pet.image_url}
                                                alt={inquiry.pet.name}
                                                className="object-cover h-full w-full"
                                            />
                                        ) : (
                                            <div className="h-full w-full flex items-center justify-center text-xl">🐾</div>
                                        )}
                                    </div>

                                    <div className="flex-grow text-center md:text-left">
                                        <h3 className="font-semibold text-lg">{inquiry.pet?.name || 'Unknown Pet'}</h3>
                                        <p className="text-sm text-muted-foreground">{inquiry.pet?.breed}</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Applied on {new Date(inquiry.created_at).toLocaleDateString()}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <Badge variant={inquiry.status === 'approved' ? 'success' : inquiry.status === 'rejected' ? 'error' : 'default'}>
                                            {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
                                        </Badge>
                                        <Link href={`/application-status-tracking/${inquiry.id}`}>
                                            <Button variant="outline" size="sm">
                                                View Details
                                            </Button>
                                        </Link>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
